import { ScrollView, StyleSheet, View } from "react-native";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { useRoute, RouteProp } from "@react-navigation/native";

import { WorkspaceHeader } from "../components/chatNewInterface/interfaceComponents/workspaceHeader/WorkspaceHeader";
import { UserData } from "../containers/settings/CompanionSettings/UserData";
import { ChatIntegration } from "../containers/settings/CompanionSettings/ChatIntegration";
import { HelpContainer } from "../containers/settings/CompanionSettings/HelpContainer/HelpContainer";
import CompanionLayout from "../layouts/CompanionLayout";
import { semanticColors } from "../styles/design-system";
import TabNavigator from "../components/common/general/TabNavigator/TabNavigator";
import { useAppConfig } from "../AppConfigProvider";
import { AICompanionSettings } from "../containers/settings/CompanionSettings/AICompanionSettings/AICompanionSettingsContainer";
import { NotificationSettingsContainer } from "../containers/settings/CompanionSettings/NotificationSettingsContainer/NotificationSettingsContainer";
import type { RootStackParamList } from "../navigation/navigation.types";

const ChatSettings = () => {
  const { t } = useTranslation();
  const { primaryColor } = useAppConfig();
  const route = useRoute<RouteProp<RootStackParamList, "Chat Settings">>();
  const [activeTab, setActiveTab] = useState(route.params?.initialTab ?? "account");

  useEffect(() => {
    if (route.params?.initialTab) {
      setActiveTab(route.params.initialTab);
    }
  }, [route.params?.initialTab]);

  const tabData = [
    { key: "account", title: "Account" },
    { key: "aiCompanion", title: "AI Companion" },
    { key: "notifications", title: "Notifications" },
    { key: "help", title: "Help" },
    { key: "integrations", title: "Integrations" },
  ];

  return (
    <CompanionLayout>
      <View style={styles.container}>
        <WorkspaceHeader title={t("Settings")} headingLevel={1} />
        <View style={styles.tabNavigatorWrapper}>
          <TabNavigator
            tabData={tabData}
            activeTab={activeTab}
            onTabPress={setActiveTab}
            activeTabColor={primaryColor}
            useTranslationText={false}
            containerStyle={styles.tabNavigator}
            tabPanelIdPrefix="tabpanel-"
          />
        </View>
        <ScrollView style={styles.scrollView} tabIndex={0}>
          <View style={styles.settingsSections}>
            <View
              role="tabpanel"
              aria-labelledby={`tab-account`}
              nativeID="tabpanel-account"
              style={activeTab === "account" ? styles.tabContentVisible : styles.tabContentHidden}>
              <UserData />
            </View>
            <View
              role="tabpanel"
              aria-labelledby={`tab-aiCompanion`}
              nativeID="tabpanel-aiCompanion"
              style={activeTab === "aiCompanion" ? styles.tabContentVisible : styles.tabContentHidden}>
              <AICompanionSettings />
            </View>
            <View
              role="tabpanel"
              aria-labelledby={`tab-notifications`}
              nativeID="tabpanel-notifications"
              style={activeTab === "notifications" ? styles.tabContentVisible : styles.tabContentHidden}>
              <NotificationSettingsContainer />
            </View>
            <View
              role="tabpanel"
              aria-labelledby={`tab-help`}
              nativeID="tabpanel-help"
              style={activeTab === "help" ? styles.tabContentVisible : styles.tabContentHidden}>
              <HelpContainer />
            </View>
            <View
              role="tabpanel"
              aria-labelledby={`tab-integrations`}
              nativeID="tabpanel-integrations"
              style={activeTab === "integrations" ? styles.tabContentVisible : styles.tabContentHidden}>
              <ChatIntegration />
            </View>
          </View>
        </ScrollView>
      </View>
    </CompanionLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    height: "100%",
    display: "flex",
    // @ts-expect-error - works on web
    maxHeight: "100vh",
    overflow: "hidden",
    backgroundColor: semanticColors.surface.light,
  },
  tabNavigatorWrapper: {
    backgroundColor: semanticColors.surface.light,
    paddingHorizontal: 24,
  },
  tabNavigator: {
    width: "100%",
    maxWidth: 624,
    marginHorizontal: "auto",
  },
  scrollView: {
    flex: 1,
  },
  settingsSections: {
    width: "100%",
    maxWidth: 624,
    marginHorizontal: "auto",
    display: "flex",
    gap: 32,
    paddingVertical: 24,
  },
  tabContentVisible: {
    display: "flex",
  },
  tabContentHidden: {
    display: "none",
  },
});

export default ChatSettings;
